import { kleur } from '../theme/tokens';
import { gedeeld } from '../theme/stijlen';
import React, { useState, useEffect } from 'react';
import { Users, MessageSquare, Sliders, LogOut, Plus, Save, Trash2, FileText, RefreshCw, Edit2 } from 'lucide-react';
import { maakApi } from '../api/client';
import { Portret, portretUrl } from '../components/Portret';

// Constanten voor woordlimiet
const MAX_WOORDEN_BASIS = 1000;
const TYPES_MET_LIMIET = ['PRO', 'CONTRA'];

function AdminPanel({ user, token, onLogout, apiUrl }) {
  // Helper functie voor woordtelling
  const telWoorden = (tekst) => tekst ? tekst.trim().split(/\s+/).filter(w => w).length : 0;

  const { verzoek } = maakApi({ apiUrl, token, bijVerlopenToken: onLogout });
  const [activeTab, setActiveTab] = useState('stellingen');
  const [stellingen, setStellingen] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [agentRollen, setAgentRollen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStelling, setSelectedStelling] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showBronForm, setShowBronForm] = useState(false);
  const [bronBestand, setBronBestand] = useState(null);
  const [selectedBron, setSelectedBron] = useState(null);
  const [selectedBronCategory, setSelectedBronCategory] = useState(null);
  const [editingBron, setEditingBron] = useState(null);
  const [newStelling, setNewStelling] = useState({ titel: '', beschrijving: '' });
  const [newBron, setNewBron] = useState({ naam: '', inhoud: '', type: 'PRO', titel_voor_leerlingen: '' });
  const [newPersona, setNewPersona] = useState({ naam: '', korte_omschrijving: '', profiel: '', portret: '', geslacht: '', leeftijd: '', geboorteplaats: '', woonplaats: '', samenlevingsvorm: '', werk: '', opleiding: '', inkomenspositie: '', migratieachtergrond: '', bijzonderheden: '', achtergrond: '', persoonlijkheid: '', spreekstijl: '', vast_type: '' });
  const [editingPersona, setEditingPersona] = useState(null);
  const [portretten, setPortretten] = useState({ basis: [], uploads: [] });
  const [portretFout, setPortretFout] = useState('');
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [newSchool, setNewSchool] = useState({ naam: '', brinnummer: '' });
  const [newTeacher, setNewTeacher] = useState({ naam: '', username: '', password: '', email: '', school_id: '' });
  const [showSchoolForm, setShowSchoolForm] = useState(false);
  const [showTeacherForm, setShowTeacherForm] = useState(false);
  const [logSchools, setLogSchools] = useState([]);
  const [logClasses, setLogClasses] = useState([]);
  const [logSessions, setLogSessions] = useState([]);
  const [selectedLogSchool, setSelectedLogSchool] = useState('');
  const [selectedLogClass, setSelectedLogClass] = useState('');
  const [editingTeacher, setEditingTeacher] = useState(null);
  // Prompts state
  const [prompts, setPrompts] = useState([]);
  const [promptCategories, setPromptCategories] = useState([]);
  const [selectedPromptCategory, setSelectedPromptCategory] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showPromptForm, setShowPromptForm] = useState(false);
  const [newPrompt, setNewPrompt] = useState({ category: '', key: '', name: '', description: '', prompt_text: '', sort_order: 0 });

  useEffect(() => {
    if (activeTab === 'stellingen') fetchStellingen();
    else if (activeTab === 'personas') { fetchPersonas(); fetchPortretten(); }
    else if (activeTab === 'agentrollen') fetchAgentRollen();
    else if (activeTab === 'prompts') { fetchPromptCategories(); fetchPrompts(); }
    // Alleen bij een tabwissel; de fetch-functies hierboven veranderen bij
    // elke render van identiteit en horen daarom niet in de lijst.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchStellingen = async () => {
    setLoading(true);
    try {
      const response = await verzoek('/admin/statements');
      const data = await response.json();
      if (data.success) setStellingen(data.data);
    } catch (err) { console.error('Fout:', err); }
    finally { setLoading(false); }
  };

  const fetchPersonas = async () => {
    setLoading(true);
    try {
      const response = await verzoek('/admin/personas');
      const data = await response.json();
      if (data.success) setPersonas(data.data.standaard || []);
    } catch (err) { console.error('Fout:', err); }
    finally { setLoading(false); }
  };

  const fetchAgentRollen = async () => {
    setLoading(true);
    try {
      const response = await verzoek('/admin/agent-rollen');
      const data = await response.json();
      if (data.success) setAgentRollen(data.data);
    } catch (err) { console.error('Fout:', err); }
    finally { setLoading(false); }
  };

  const fetchPromptCategories = async () => {
    try {
      const response = await verzoek('/admin/prompts/categories');
      const data = await response.json();
      if (data.success) setPromptCategories(data.data);
    } catch (err) { console.error('Fout bij laden categorieën:', err); }
  };

  const fetchPrompts = async (category = null) => {
    setLoading(true);
    try {
      const response = await verzoek(category ? '/admin/prompts?category=' + category : '/admin/prompts');
      const data = await response.json();
      if (data.success) setPrompts(data.data);
    } catch (err) { console.error('Fout bij laden prompts:', err); }
    finally { setLoading(false); }
  };

  const updatePrompt = async () => {
    try {
      const response = await verzoek('/admin/prompts/' + editingPrompt.id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingPrompt.name,
          description: editingPrompt.description,
          prompt_text: editingPrompt.prompt_text,
          is_active: editingPrompt.is_active,
          sort_order: editingPrompt.sort_order
        })
      });
      const data = await response.json();
      if (data.success) {
        setEditingPrompt(null);
        fetchPrompts(selectedPromptCategory);
        if (selectedPrompt?.id === editingPrompt.id) {
          setSelectedPrompt(data.data);
        }
      }
    } catch (err) { console.error('Fout bij opslaan prompt:', err); }
  };

  const createPromptHandler = async (e) => {
    e.preventDefault();
    try {
      const response = await verzoek('/admin/prompts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPrompt)
      });
      const data = await response.json();
      if (data.success) {
        setNewPrompt({ category: '', key: '', name: '', description: '', prompt_text: '', sort_order: 0 });
        setShowPromptForm(false);
        fetchPrompts(selectedPromptCategory);
        fetchPromptCategories();
      } else {
        alert(data.detail || 'Fout bij aanmaken prompt');
      }
    } catch (err) { console.error('Fout bij aanmaken prompt:', err); }
  };

  const deletePrompt = async (promptId) => {
    if (!window.confirm('Weet je zeker dat je deze prompt wilt verwijderen?')) return;
    try {
      const response = await verzoek('/admin/prompts/' + promptId, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        setSelectedPrompt(null);
        fetchPrompts(selectedPromptCategory);
        fetchPromptCategories();
      } else {
        alert(data.detail || 'Fout bij verwijderen');
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const resetPrompt = async (promptId) => {
    if (!window.confirm('Weet je zeker dat je deze prompt wilt resetten naar de standaard waarde?')) return;
    try {
      const response = await verzoek('/admin/prompts/reset/' + promptId, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        setSelectedPrompt(data.data);
        fetchPrompts(selectedPromptCategory);
      } else {
        alert(data.detail || 'Fout bij resetten');
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const seedPrompts = async () => {
    try {
      const response = await verzoek('/admin/prompts/seed', { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        alert(data.message);
        fetchPrompts(selectedPromptCategory);
        fetchPromptCategories();
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const createStelling = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({ titel: newStelling.titel, beschrijving: newStelling.beschrijving, teacher_id: user.id });
      const response = await verzoek('/admin/statements?' + params, { method: 'POST' });
      const data = await response.json();
      if (data.success) { setNewStelling({ titel: '', beschrijving: '' }); setShowForm(false); fetchStellingen(); }
    } catch (err) { console.error('Fout:', err); }
  };

  const viewStelling = async (stellingId) => {
    try {
      const response = await verzoek('/admin/statements/' + stellingId);
      const data = await response.json();
      if (data.success) setSelectedStelling(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const deleteStelling = async (stellingId) => {
    if (!window.confirm('Weet je zeker?')) return;
    try {
      const response = await verzoek('/admin/statements/' + stellingId, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) { fetchStellingen(); setSelectedStelling(null); }
    } catch (err) { console.error('Fout:', err); }
  };

  const addBron = async (e) => {
    e.preventDefault();
    const stellingId = selectedStelling.id;
    try {
      if (bronBestand && bronBestand !== 'selecteer') {
        const formData = new FormData();
        formData.append('bestand', bronBestand);
        formData.append('type', newBron.type);
        formData.append('titel_voor_leerlingen', newBron.titel_voor_leerlingen);
        const response = await verzoek('/admin/statements/' + stellingId + '/sources/upload', {
          method: 'POST',
          body: formData
        });
        const data = await response.json();
        if (data.success) { setNewBron({ naam: '', inhoud: '', type: 'PRO', titel_voor_leerlingen: '' }); setBronBestand(null); setShowBronForm(false); await viewStelling(stellingId); }
        else { alert(data.detail || 'Fout bij uploaden'); }
      } else {
        const response = await verzoek('/admin/statements/' + stellingId + '/sources', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ naam: newBron.naam, inhoud: newBron.inhoud, type: newBron.type })
        });
        const data = await response.json();
        if (data.success) { setNewBron({ naam: '', inhoud: '', type: 'PRO', titel_voor_leerlingen: '' }); setShowBronForm(false); await viewStelling(stellingId); }
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const deleteBron = async (bronId) => {
    if (!window.confirm('Weet je zeker?')) return;
    const stellingId = selectedStelling.id;
    try {
      const response = await verzoek('/admin/statements/sources/' + bronId, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) await viewStelling(stellingId);
    } catch (err) { console.error('Fout:', err); }
  };

  const viewBron = async (bronId) => {
    try {
      const response = await verzoek('/admin/statements/sources/' + bronId);
      const data = await response.json();
      if (data.success) setSelectedBron(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const updateBron = async (bronId, naam, inhoud) => {
    try {
      const params = new URLSearchParams();
      if (naam) params.append('naam', naam);
      if (inhoud !== undefined) params.append('inhoud', inhoud);
      const response = await verzoek('/admin/statements/sources/' + bronId + '?' + params, { method: 'PUT' });
      const data = await response.json();
      if (data.success) {
        setEditingBron(null);
        setSelectedBron(data.data);
        await viewStelling(selectedStelling.id);
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const viewPersona = async (personaId) => {
    try {
      const response = await verzoek('/admin/personas/' + personaId);
      const data = await response.json();
      if (data.success) setSelectedPersona(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const fetchPortretten = async () => {
    try {
      const response = await verzoek('/admin/portretten');
      const data = await response.json();
      if (data.success) setPortretten(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const uploadPortret = async (bestand, zetPortret) => {
    if (!bestand) return;
    setPortretFout('');
    const formData = new FormData();
    formData.append('bestand', bestand);
    try {
      const response = await verzoek('/admin/portretten', { method: 'POST', body: formData });
      const data = await response.json();
      if (data.success) {
        zetPortret(data.data.portret);
        fetchPortretten();
      } else {
        setPortretFout(data.detail || 'Het uploaden is niet gelukt.');
      }
    } catch (err) {
      setPortretFout('Het uploaden is niet gelukt.');
    }
  };

  // De keuzegalerij: de basisset uit backend/portretten/ plus wat eerder is
  // geupload. Wordt zowel in het nieuwe-persona-formulier als bij het
  // bewerken gebruikt, vandaar een eigen functie in plaats van twee blokken.
  const portretKiezer = (huidig, zetPortret) => (
    <div style={styles.portretBlok}>
      <label style={styles.label}>Portret (zien de leerlingen)</label>
      <div style={styles.portretGalerij}>
        <button type="button" onClick={() => zetPortret('')}
          style={!huidig ? styles.portretLeegActief : styles.portretLeeg} title="Geen portret">
          geen
        </button>
        {[...portretten.basis, ...portretten.uploads].map(naam => (
          <button key={naam} type="button" onClick={() => zetPortret(naam)}
            style={huidig === naam ? styles.portretKeuzeActief : styles.portretKeuze} title={naam}>
            <img src={portretUrl(apiUrl, naam)} alt={naam} style={styles.portretMini} />
          </button>
        ))}
      </div>
      <label style={styles.portretUploadLabel}>
        Eigen portret uploaden (PNG, JPG of WEBP, max 2 MB)
        <input type="file" accept=".png,.jpg,.jpeg,.webp"
          onChange={(e) => { uploadPortret(e.target.files[0], zetPortret); e.target.value = ''; }}
          style={styles.input} />
      </label>
      {portretFout && <p style={styles.portretFout}>{portretFout}</p>}
      {portretten.basis.length === 0 && (
        <p style={styles.beschrijving}>
          De basisset is nog leeg. Zet de bestanden in <code>backend/portretten/</code> en rol de code uit.
        </p>
      )}
    </div>
  );

  const createPersona = async (e) => {
    e.preventDefault();
    try {
      const response = await verzoek('/admin/personas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPersona)
      });
      const data = await response.json();
      if (data.success) { setNewPersona({ naam: '', korte_omschrijving: '', profiel: '', portret: '', geslacht: '', leeftijd: '', woonplaats: '', werk: '', achtergrond: '', persoonlijkheid: '', spreekstijl: '', vast_type: '' }); setShowForm(false); fetchPersonas(); }
    } catch (err) { console.error('Fout:', err); }
  };

  const updatePersona = async () => {
    try {
      const response = await verzoek('/admin/personas/' + editingPersona.id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingPersona)
      });
      const data = await response.json();
      if (data.success) { 
        setEditingPersona(null); 
        fetchPersonas(); 
        if (selectedPersona?.id === editingPersona.id) {
          viewPersona(editingPersona.id);
        }
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const deletePersona = async (personaId) => {
    if (!window.confirm('Weet je zeker dat je deze persona wilt verwijderen?')) return;
    try {
      const response = await verzoek('/admin/personas/' + personaId, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSelectedPersona(null);
        fetchPersonas();
      }
    } catch (err) { console.error('Fout:', err); }
  };
  
  const updateAgentRol = async (rol) => {
    try {
      const response = await verzoek('/admin/agent-rollen/' + rol.id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rol)
      });
      const data = await response.json();
      if (data.success) fetchAgentRollen();
    } catch (err) { console.error('Fout:', err); }
  };

// === BEHEERDERS FUNCTIES ===
  const fetchSchools = async () => {
    try {
      const response = await verzoek('/admin/schools');
      const data = await response.json();
      if (data.success) setSchools(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const createSchool = async (e) => {
    e.preventDefault();
    try {
      const response = await verzoek('/admin/schools', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSchool)
      });
      const data = await response.json();
      if (data.success) {
        setNewSchool({ naam: '', brinnummer: '' });
        setShowSchoolForm(false);
        fetchSchools();
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const deleteSchool = async (schoolId) => {
    if (!window.confirm('Weet je zeker dat je deze school wilt verwijderen?\n\nDit verwijdert ook:\n• Alle leerkrachten\n• Alle klassen\n• Alle leerlingen\n• Alle sessies en discussies\n• Alle stellingen en bronnen\n\nDeze actie kan niet ongedaan worden gemaakt.')) return;
    try {
      const response = await verzoek('/admin/schools/' + schoolId, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        setSelectedSchool(null);
        fetchSchools();
        alert('School en alle bijbehorende gegevens zijn verwijderd.');
      } else {
        alert('Fout bij verwijderen: ' + (data.detail || 'Onbekende fout'));
      }
    } catch (err) {
      console.error('Fout:', err);
      alert('Er is een fout opgetreden bij het verwijderen van de school. Probeer het opnieuw.');
    }
  };

const createTeacher = async (e) => {
    e.preventDefault();
    const teacherData = { ...newTeacher, school_id: selectedSchool.id };
    try {
      const response = await verzoek('/admin/teachers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(teacherData)
      });
      const data = await response.json();
      if (data.success) {
        // Toon inloggegevens
        alert('Leerkracht aangemaakt!\n\nInloggegevens:\nGebruikersnaam: ' + teacherData.username + '\nWachtwoord: ' + teacherData.password + '\n\nBewaar deze gegevens veilig!');
        setNewTeacher({ naam: '', username: '', password: '', school_id: '' });
        setShowTeacherForm(false);
        // Herlaad de scholen en selecteer de huidige school opnieuw
        const schoolsResponse = await verzoek('/admin/schools');
        const schoolsData = await schoolsResponse.json();
        if (schoolsData.success) {
          setSchools(schoolsData.data);
          const updatedSchool = schoolsData.data.find(s => s.id === selectedSchool.id);
          if (updatedSchool) setSelectedSchool(updatedSchool);
        }
      } else {
        alert('Fout bij toevoegen: ' + (data.detail || 'Onbekende fout'));
      }
    } catch (err) { 
      console.error('Fout:', err); 
      alert('Er ging iets mis bij het toevoegen van de leerkracht');
    }
  };

const deleteTeacher = async (teacherId) => {
    if (!window.confirm('Weet je zeker dat je deze leerkracht wilt verwijderen?')) return;
    try {
      const response = await verzoek('/admin/teachers/' + teacherId, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        // Herlaad de scholen en selecteer de huidige school opnieuw
        const schoolsResponse = await verzoek('/admin/schools');
        const schoolsData = await schoolsResponse.json();
        if (schoolsData.success) {
          setSchools(schoolsData.data);
          const updatedSchool = schoolsData.data.find(s => s.id === selectedSchool.id);
          if (updatedSchool) setSelectedSchool(updatedSchool);
        }
      }
    } catch (err) { console.error('Fout:', err); }
  };

const updateTeacher = async (e) => {
    e.preventDefault();
    try {
      const response = await verzoek('/admin/teachers/' + editingTeacher.id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          naam: editingTeacher.naam,
          username: editingTeacher.username,
          password: editingTeacher.newPassword || null
        })
      });
      const data = await response.json();
      if (data.success) {
        setEditingTeacher(null);
        // Herlaad de scholen en selecteer de huidige school opnieuw
        const schoolsResponse = await verzoek('/admin/schools');
        const schoolsData = await schoolsResponse.json();
        if (schoolsData.success) {
          setSchools(schoolsData.data);
          const updatedSchool = schoolsData.data.find(s => s.id === selectedSchool.id);
          if (updatedSchool) setSelectedSchool(updatedSchool);
        }
      }
    } catch (err) { console.error('Fout:', err); }
  };


  // === LOGBESTANDEN FUNCTIES ===
  const fetchLogSchools = async () => {
    try {
      const response = await verzoek('/admin/logs/schools');
      const data = await response.json();
      if (data.success) setLogSchools(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const fetchLogClasses = async (schoolId) => {
    try {
      const response = await verzoek('/admin/logs/classes/' + schoolId);
      const data = await response.json();
      if (data.success) setLogClasses(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const fetchLogSessions = async (classId) => {
    try {
      const response = await verzoek('/admin/logs/sessions/' + classId);
      const data = await response.json();
      if (data.success) setLogSessions(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const downloadLog = async (sessionId) => {
    try {
      const response = await verzoek('/admin/logs/download/' + sessionId);
      const data = await response.json();
      if (data.success) {
        const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sessie_log_' + sessionId + '.json';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) { console.error('Fout:', err); }
  };


  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <img src="/socrates_logo.png" alt="Socrates" style={{ height: '125px' }} />
        <div style={styles.userInfo}>
          <span>{user.naam}</span>
          <button onClick={onLogout} style={styles.logoutBtn}><LogOut size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Uitloggen</button>
        </div>
      </div>

      <div style={styles.tabs}>
        <button style={activeTab === 'stellingen' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('stellingen')}><MessageSquare size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Stellingen</button>
        <button style={activeTab === 'personas' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('personas')}><Users size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Persona's</button>
        <button style={activeTab === 'agentrollen' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('agentrollen')}><Sliders size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Agent-rollen</button>
        <button style={activeTab === 'prompts' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('prompts')}><MessageSquare size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Prompts</button>
        <button style={activeTab === 'beheerders' ? styles.tabActive : styles.tab} onClick={() => { setActiveTab('beheerders'); fetchSchools(); }}><Users size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Beheerders</button>
        <button style={activeTab === 'logs' ? styles.tabActive : styles.tab} onClick={() => { setActiveTab('logs'); fetchLogSchools(); }}><FileText size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Logbestanden</button>
    </div>

      <div style={styles.content}>
        {/* STELLINGEN TAB */}
        {activeTab === 'stellingen' && (
          <div style={styles.splitView}>
            <div style={styles.listPanel}>
              <div style={styles.panelHeader}>
                <h2>De stellingen</h2>
                <button onClick={() => setShowForm(true)} style={styles.addBtn}><Plus size={16} /> Nieuwe Stelling</button>
              </div>
              {showForm && (
                <form onSubmit={createStelling} style={styles.form}>
                  <input type="text" placeholder="Stelling" value={newStelling.titel} onChange={(e) => setNewStelling({...newStelling, titel: e.target.value})} style={styles.input} required />
                  <textarea placeholder="Beschrijving" value={newStelling.beschrijving} onChange={(e) => setNewStelling({...newStelling, beschrijving: e.target.value})} style={{...styles.input, minHeight: '80px'}} />
                  <div style={styles.formButtons}>
                    <button type="submit" style={styles.saveBtn}><Save size={16} /> Opslaan</button>
                    <button type="button" onClick={() => setShowForm(false)} style={styles.cancelBtn}>Annuleren</button>
                  </div>
                </form>
              )}
              {loading ? <p>Laden...</p> : stellingen.length === 0 ? <p style={styles.empty}>Nog geen stellingen.</p> : (
                <ul style={styles.list}>
                  {stellingen.map((stelling) => (
                    <li key={stelling.id} style={selectedStelling?.id === stelling.id ? styles.listItemActive : styles.listItem} onClick={() => viewStelling(stelling.id)}>
                      <span>{stelling.titel}</span>
                      <button onClick={(e) => { e.stopPropagation(); deleteStelling(stelling.id); }} style={styles.deleteBtn}><Trash2 size={16} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={styles.detailPanel}>
              {selectedStelling ? (
                <>
                  <h2>{selectedStelling.titel}</h2>
                  {selectedStelling.beschrijving && <p style={styles.beschrijving}>{selectedStelling.beschrijving}</p>}
                  <div style={styles.panelHeader}>
                    <h3 style={styles.subheader}>Bronnen</h3>
                    <button onClick={() => setShowBronForm(true)} style={styles.addBtn}><Plus size={16} /> Bron</button>
                  </div>
                  {showBronForm && (
                    <form onSubmit={addBron} style={styles.form}>
                      <div style={styles.formTabs}>
                        <button type="button" onClick={() => setBronBestand(null)} style={!bronBestand ? styles.formTabActive : styles.formTab}>Tekst invoeren</button>
                        <button type="button" onClick={() => setBronBestand('selecteer')} style={bronBestand ? styles.formTabActive : styles.formTab}>Bestand uploaden</button>
                      </div>
                      {!bronBestand ? (
                        <>
                          <input type="text" placeholder="Naam van de bron" value={newBron.naam} onChange={(e) => setNewBron({...newBron, naam: e.target.value})} style={styles.input} required />
                          <textarea placeholder="Inhoud" value={newBron.inhoud} onChange={(e) => setNewBron({...newBron, inhoud: e.target.value})} style={{...styles.input, minHeight: '100px'}} required />
                          {TYPES_MET_LIMIET.includes(newBron.type) && (
                            <div style={{
                              ...styles.woordTeller,
                              color: telWoorden(newBron.inhoud) > MAX_WOORDEN_BASIS ? kleur.rood : kleur.stil
                            }}>
                              {telWoorden(newBron.inhoud)} / {MAX_WOORDEN_BASIS} woorden
                              {telWoorden(newBron.inhoud) > MAX_WOORDEN_BASIS && (
                                <span style={styles.woordWarning}> - Te lang! Gebruik PRO_EXPERT of CONTRA_EXPERT voor langere teksten.</span>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <input type="file" accept=".pdf,.docx" onChange={(e) => setBronBestand(e.target.files[0])} style={styles.input} required />
                          <input type="text" placeholder="Titel voor leerlingen" value={newBron.titel_voor_leerlingen} onChange={(e) => setNewBron({...newBron, titel_voor_leerlingen: e.target.value})} style={styles.input} required />
                        </>
                      )}
		      <select value={newBron.type} onChange={(e) => setNewBron({...newBron, type: e.target.value})} style={styles.input}>
                        <option value="PRO">PRO</option>
                        <option value="CONTRA">CONTRA</option>
                        <option value="PRO_EXPERT">PRO EXPERT</option>
                        <option value="CONTRA_EXPERT">CONTRA EXPERT</option>
                      </select>
                      <div style={styles.formButtons}>
                        <button type="submit" style={styles.saveBtn}><Save size={16} /> Toevoegen</button>
                        <button type="button" onClick={() => { setShowBronForm(false); setBronBestand(null); }} style={styles.cancelBtn}>Annuleren</button>
                      </div>
                    </form>
                  )}
                    <div style={styles.bronnenGrid}>
                    {['PRO', 'CONTRA', 'PRO_EXPERT', 'CONTRA_EXPERT'].map(cat => (
                      <div key={cat} style={styles.bronnenColumn} onClick={() => setSelectedBronCategory(cat)}>
                        <h4 style={styles.bronCategoryHeader}>{cat.replace('_', ' ')} <span style={styles.bronCount}>({selectedStelling.sources?.[cat]?.length || 0})</span></h4>
                        <p style={styles.bronPreviewText}>Klik om bronnen te bekijken</p>
                      </div>
                    ))}
                  </div>

                  {/* Categorie Modal */}
                  {selectedBronCategory && (
                    <div style={styles.categoryModal}>
                      <div style={styles.categoryModalHeader}>
                        <h3>{selectedBronCategory.replace('_', ' ')} bronnen</h3>
                        <button onClick={() => { setSelectedBronCategory(null); setSelectedBron(null); setEditingBron(null); }} style={styles.closeBtn}>×</button>
                      </div>
                      <div style={styles.categoryBronList}>
                        {selectedStelling.sources?.[selectedBronCategory]?.length > 0 ? (
                          selectedStelling.sources[selectedBronCategory].map(bron => (
                            <div key={bron.id} style={{...styles.categoryBronItem, ...(selectedBron?.id === bron.id ? styles.categoryBronItemActive : {})}}>
                              <div style={styles.categoryBronHeader} onClick={() => { viewBron(bron.id); setEditingBron(null); }}>
                                <span style={styles.categoryBronNaam}>{bron.naam}</span>
                                <button onClick={(e) => { e.stopPropagation(); deleteBron(bron.id); }} style={styles.deleteBtn}><Trash2 size={14} /></button>
                              </div>
                            </div>
                          ))
                        ) : <p style={styles.empty}>Geen bronnen in deze categorie</p>}
                      </div>

                      {/* Bron detail/edit view */}
                      {selectedBron && (
                        <div style={styles.bronDetailPanel}>
                          {editingBron ? (
                            <>
                              <input
                                type="text"
                                value={editingBron.naam}
                                onChange={(e) => setEditingBron({...editingBron, naam: e.target.value})}
                                style={styles.input}
                                placeholder="Naam"
                              />
                              <textarea
                                value={editingBron.inhoud}
                                onChange={(e) => setEditingBron({...editingBron, inhoud: e.target.value})}
                                style={{...styles.input, minHeight: '300px', fontFamily: 'inherit'}}
                              />
                              {TYPES_MET_LIMIET.includes(editingBron.type) && (
                                <div style={{
                                  ...styles.woordTeller,
                                  color: telWoorden(editingBron.inhoud) > MAX_WOORDEN_BASIS ? kleur.rood : kleur.stil
                                }}>
                                  {telWoorden(editingBron.inhoud)} / {MAX_WOORDEN_BASIS} woorden
                                  {telWoorden(editingBron.inhoud) > MAX_WOORDEN_BASIS && (
                                    <span style={styles.woordWarning}> - Te lang!</span>
                                  )}
                                </div>
                              )}
                              <div style={styles.formButtons}>
                                <button
                                  onClick={() => updateBron(editingBron.id, editingBron.naam, editingBron.inhoud)}
                                  style={styles.saveBtn}
                                  disabled={TYPES_MET_LIMIET.includes(editingBron.type) && telWoorden(editingBron.inhoud) > MAX_WOORDEN_BASIS}
                                >
                                  <Save size={16} /> Opslaan
                                </button>
                                <button onClick={() => setEditingBron(null)} style={styles.cancelBtn}>Annuleren</button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={styles.bronDetailHeader}>
                                <h4>{selectedBron.naam}</h4>
                                {!selectedBron.bestandsnaam && (
                                  <button onClick={() => setEditingBron({...selectedBron})} style={styles.editBtn}><Edit2 size={14} /> Bewerken</button>
                                )}
                              </div>
                              {selectedBron.bestandsnaam && <p style={styles.bronInfo}><FileText size={14} /> Bestand: {selectedBron.bestandsnaam}</p>}
                              {selectedBron.titel_voor_leerlingen && <p style={styles.bronInfo}>Titel voor leerlingen: {selectedBron.titel_voor_leerlingen}</p>}
                              <pre style={styles.bronTekst}>{selectedBron.inhoud}</pre>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                                  </>
              ) : <p style={styles.empty}>Selecteer een stelling</p>}
            </div>
          </div>
        )}

       {/* PERSONAS TAB */}
        {activeTab === 'personas' && (
          <div style={styles.splitView}>
            <div style={styles.listPanel}>
              <div style={styles.panelHeader}>
                <h2>Persona's</h2>
                <button onClick={() => setShowForm(true)} style={styles.addBtn}><Plus size={16} /> Nieuwe Persona</button>
              </div>
              {showForm && (
                <form onSubmit={createPersona} style={styles.form}>
                  <input type="text" placeholder="Naam" value={newPersona.naam} onChange={(e) => setNewPersona({...newPersona, naam: e.target.value})} style={styles.input} required />
                  <textarea placeholder="Profiel (zichtbaar voor leerlingen)" value={newPersona.profiel} onChange={(e) => setNewPersona({...newPersona, profiel: e.target.value})} style={{...styles.input, minHeight: '60px'}} />
                  <input type="text" placeholder="Korte omschrijving" value={newPersona.korte_omschrijving} onChange={(e) => setNewPersona({...newPersona, korte_omschrijving: e.target.value})} style={styles.input} required />
                  {portretKiezer(newPersona.portret, (naam) => setNewPersona({...newPersona, portret: naam}))}
                  <div style={styles.row}>
                    <input type="text" placeholder="Geslacht" value={newPersona.geslacht} onChange={(e) => setNewPersona({...newPersona, geslacht: e.target.value})} style={styles.inputSmall} />
                    <input type="number" placeholder="Leeftijd" value={newPersona.leeftijd} onChange={(e) => setNewPersona({...newPersona, leeftijd: e.target.value})} style={styles.inputSmall} />
                  </div>
                  <div style={styles.row}>
                    <input type="text" placeholder="Geboorteplaats" value={newPersona.geboorteplaats} onChange={(e) => setNewPersona({...newPersona, geboorteplaats: e.target.value})} style={styles.inputSmall} />
                    <input type="text" placeholder="Woonplaats" value={newPersona.woonplaats} onChange={(e) => setNewPersona({...newPersona, woonplaats: e.target.value})} style={styles.inputSmall} />
                  </div>
                  <div style={styles.row}>
                    <input type="text" placeholder="Samenlevingsvorm" value={newPersona.samenlevingsvorm} onChange={(e) => setNewPersona({...newPersona, samenlevingsvorm: e.target.value})} style={styles.inputSmall} />
                    <input type="text" placeholder="Werk" value={newPersona.werk} onChange={(e) => setNewPersona({...newPersona, werk: e.target.value})} style={styles.inputSmall} />
                  </div>
                  <input type="text" placeholder="Opleiding" value={newPersona.opleiding} onChange={(e) => setNewPersona({...newPersona, opleiding: e.target.value})} style={styles.input} />
                  <input type="text" placeholder="Inkomenspositie" value={newPersona.inkomenspositie} onChange={(e) => setNewPersona({...newPersona, inkomenspositie: e.target.value})} style={styles.input} />
                  <input type="text" placeholder="Migratieachtergrond" value={newPersona.migratieachtergrond} onChange={(e) => setNewPersona({...newPersona, migratieachtergrond: e.target.value})} style={styles.input} />                 
                  <textarea placeholder="Bijzonderheden en interesses" value={newPersona.bijzonderheden} onChange={(e) => setNewPersona({...newPersona, bijzonderheden: e.target.value})} style={{...styles.input, minHeight: '60px'}} />
                  <textarea placeholder="Waarden & Overtuiging" value={newPersona.achtergrond} onChange={(e) => setNewPersona({...newPersona, achtergrond: e.target.value})} style={{...styles.input, minHeight: '60px'}} />
                  <textarea placeholder="Persoonlijkheid" value={newPersona.persoonlijkheid} onChange={(e) => setNewPersona({...newPersona, persoonlijkheid: e.target.value})} style={{...styles.input, minHeight: '60px'}} />
                  <textarea placeholder="Spreekstijl" value={newPersona.spreekstijl} onChange={(e) => setNewPersona({...newPersona, spreekstijl: e.target.value})} style={{...styles.input, minHeight: '60px'}} />
                  <select value={newPersona.vast_type} onChange={(e) => setNewPersona({...newPersona, vast_type: e.target.value})} style={styles.input}>
                    <option value="">-- Kies agent-rol --</option>
                    <option value="discussieleider">Discussieleider</option>
                    <option value="kennis_pro">Kennis pro</option>
                    <option value="kennis_contra">Kennis contra</option>
                    <option value="expert_pro">Expert pro</option>
                    <option value="expert_contra">Expert contra</option>
                    <option value="twijfelaar">Twijfelaar</option>
                    <option value="echoput">Echoput</option>
                    <option value="afleider">Afleider</option>
                  </select>
                  <div style={styles.formButtons}>
                    <button type="submit" style={styles.saveBtn}><Save size={16} /> Opslaan</button>
                    <button type="button" onClick={() => setShowForm(false)} style={styles.cancelBtn}>Annuleren</button>
                  </div>
                </form>
              )}
              {loading ? <p>Laden...</p> : personas.length === 0 ? <p style={styles.empty}>Geen persona's.</p> : (
                <ul style={styles.list}>
                  {personas.map((persona) => (
                    <li key={persona.id} style={selectedPersona?.id === persona.id ? styles.listItemActive : styles.listItem} onClick={() => viewPersona(persona.id)}>
                      <span>{persona.naam}</span>
                      {persona.vast_type && <span style={styles.badge}>{persona.vast_type}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={styles.detailPanel}>
              {selectedPersona ? (
                editingPersona ? (
                  <div style={styles.editForm}>
                    <h2>Bewerken: {selectedPersona.naam}</h2>
                    <div style={{height: '12px'}}></div>
                    <label style={styles.label}>Profiel (zichtbaar voor leerlingen)</label>
                    <textarea value={editingPersona.profiel || ''} onChange={(e) => setEditingPersona({...editingPersona, profiel: e.target.value})} style={{...styles.input, minHeight: '80px'}} />
                    
                    <label style={styles.label}>Korte omschrijving</label>
                    <input type="text" value={editingPersona.korte_omschrijving || ''} onChange={(e) => setEditingPersona({...editingPersona, korte_omschrijving: e.target.value})} style={styles.input} />
                    {portretKiezer(editingPersona.portret, (naam) => setEditingPersona({...editingPersona, portret: naam}))}
                    
                    <div style={styles.infoGrid}>
                      <div>
                        <label style={styles.label}>Geslacht</label>
                        <input type="text" value={editingPersona.geslacht || ''} onChange={(e) => setEditingPersona({...editingPersona, geslacht: e.target.value})} style={styles.input} />
                      </div>
                      <div>
                        <label style={styles.label}>Leeftijd</label>
                        <input type="number" value={editingPersona.leeftijd || ''} onChange={(e) => setEditingPersona({...editingPersona, leeftijd: e.target.value})} style={styles.input} />
                      </div>
                      <div>
                        <label style={styles.label}>Geboorteplaats</label>
                        <input type="text" value={editingPersona.geboorteplaats || ''} onChange={(e) => setEditingPersona({...editingPersona, geboorteplaats: e.target.value})} style={styles.input} />
                      </div>
                      <div>
                        <label style={styles.label}>Woonplaats</label>
                        <input type="text" value={editingPersona.woonplaats || ''} onChange={(e) => setEditingPersona({...editingPersona, woonplaats: e.target.value})} style={styles.input} />
                      </div>
                      <div>
                        <label style={styles.label}>Samenlevingsvorm</label>
                        <input type="text" value={editingPersona.samenlevingsvorm || ''} onChange={(e) => setEditingPersona({...editingPersona, samenlevingsvorm: e.target.value})} style={styles.input} />
                      </div>
                      <div>
                        <label style={styles.label}>Werk</label>
                        <input type="text" value={editingPersona.werk || ''} onChange={(e) => setEditingPersona({...editingPersona, werk: e.target.value})} style={styles.input} />
                      </div>
                      <div>
                        <label style={styles.label}>Opleiding</label>
                        <input type="text" value={editingPersona.opleiding || ''} onChange={(e) => setEditingPersona({...editingPersona, opleiding: e.target.value})} style={styles.input} />
                      </div>
                      <div>
                        <label style={styles.label}>Inkomenspositie</label>
                        <input type="text" value={editingPersona.inkomenspositie || ''} onChange={(e) => setEditingPersona({...editingPersona, inkomenspositie: e.target.value})} style={styles.input} />
                      </div>
                      <div>
                        <label style={styles.label}>Migratieachtergrond</label>
                        <input type="text" value={editingPersona.migratieachtergrond || ''} onChange={(e) => setEditingPersona({...editingPersona, migratieachtergrond: e.target.value})} style={styles.input} />
                     </div>
                    </div>

                    <label style={styles.label}>Bijzonderheden en interesses</label>
                    <textarea value={editingPersona.bijzonderheden || ''} onChange={(e) => setEditingPersona({...editingPersona, bijzonderheden: e.target.value})} style={{...styles.input, minHeight: '60px'}} />
                    
                    <label style={styles.label}>Waarden & Overtuiging</label>
                    <textarea value={editingPersona.achtergrond || ''} onChange={(e) => setEditingPersona({...editingPersona, achtergrond: e.target.value})} style={{...styles.input, minHeight: '80px'}} />
                    
                    <label style={styles.label}>Persoonlijkheid</label>
                    <textarea value={editingPersona.persoonlijkheid || ''} onChange={(e) => setEditingPersona({...editingPersona, persoonlijkheid: e.target.value})} style={{...styles.input, minHeight: '80px'}} />
                    
                    <label style={styles.label}>Spreekstijl</label>
                    <textarea value={editingPersona.spreekstijl || ''} onChange={(e) => setEditingPersona({...editingPersona, spreekstijl: e.target.value})} style={{...styles.input, minHeight: '80px'}} />
                    
                    <label style={styles.label}>Agent-rol</label>
                    <select value={editingPersona.vast_type || ''} onChange={(e) => setEditingPersona({...editingPersona, vast_type: e.target.value})} style={styles.input}>
                      <option value="">-- Geen vaste rol --</option>
                      <option value="discussieleider">Discussieleider</option>
                      <option value="kennis_pro">Kennis pro</option>
                      <option value="kennis_contra">Kennis contra</option>
                      <option value="expert_pro">Expert pro</option>
                      <option value="expert_contra">Expert contra</option>
                      <option value="twijfelaar">Twijfelaar</option>
                      <option value="echoput">Echoput</option>
                      <option value="afleider">Afleider</option>
                    </select>
                    <div style={{height: '16px'}}></div>
                    <div style={styles.formButtons}>
                      <button type="button" onClick={updatePersona} style={styles.saveBtn}><Save size={16} /> Opslaan</button>
                      <button type="button" onClick={() => setEditingPersona(null)} style={styles.cancelBtn}>Annuleren</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={styles.panelHeader}>
                      <h2>
                        <Portret apiUrl={apiUrl} naam={selectedPersona.portret} grootte={40} stijl={styles.portretKop} />
                        {selectedPersona.naam}
                      </h2>
                      <div style={{display: 'flex', gap: '8px'}}>
                        <button onClick={() => setEditingPersona({...selectedPersona})} style={styles.addBtn}>Bewerken</button>
                        {selectedPersona.vast_type !== 'discussieleider' && selectedPersona.naam?.toLowerCase() !== 'socrates' && (
                        <button onClick={() => deletePersona(selectedPersona.id)} style={styles.deleteBtn}><Trash2 size={16} /> Verwijderen</button>
                        )}
                      </div>
                    </div>
                    {selectedPersona.profiel && (
                      <>
                        <h3>Profiel</h3>
                        <p>{selectedPersona.profiel}</p>
                        <div style={{height: '16px'}}></div>
                      </>
                    )}
                    
                    <h3>Korte omschrijving</h3>
                    <p>{selectedPersona.korte_omschrijving}</p>
                    <div style={{height: '16px'}}></div>                    
                    <div style={styles.infoGrid}>
                      <div><strong>Geslacht:</strong> {selectedPersona.geslacht || '-'}</div>
                      <div><strong>Leeftijd:</strong> {selectedPersona.leeftijd || '-'}</div>
                      <div><strong>Geboorteplaats:</strong> {selectedPersona.geboorteplaats || '-'}</div>
                      <div><strong>Woonplaats:</strong> {selectedPersona.woonplaats || '-'}</div>
                      <div><strong>Samenlevingsvorm:</strong> {selectedPersona.samenlevingsvorm || '-'}</div>
                      <div><strong>Werk:</strong> {selectedPersona.werk || '-'}</div>
                      <div><strong>Opleiding:</strong> {selectedPersona.opleiding || '-'}</div>
                      <div><strong>Inkomenspositie:</strong> {selectedPersona.inkomenspositie || '-'}</div>
                      <div><strong>Migratieachtergrond:</strong> {selectedPersona.migratieachtergrond || '-'}</div>                    
                    </div>
                    
                    {selectedPersona.bijzonderheden && (
                      <>
                        <h3>Bijzonderheden en interesses</h3>
                        <p>{selectedPersona.bijzonderheden}</p>
                        <div style={{height: '16px'}}></div> 
                      </>
                    )}
                    
                    <h3>Waarden & Overtuiging</h3>
                    <p>{selectedPersona.achtergrond}</p>
                    <div style={{height: '16px'}}></div>

                    <h3>Persoonlijkheid</h3>
                    <p>{selectedPersona.persoonlijkheid}</p>
                    <div style={{height: '16px'}}></div>

                    <h3>Spreekstijl</h3>
                    <p>{selectedPersona.spreekstijl}</p>
                    <div style={{height: '16px'}}></div>

                    {selectedPersona.vast_type && <p><strong>Agent-rol:</strong> {selectedPersona.vast_type}</p>}
                  </>
                )
              ) : <p style={styles.empty}>Selecteer een persona</p>}
            </div>
          </div>
        )}

        {/* AGENT-ROLLEN TAB */}
        {activeTab === 'agentrollen' && (
          <div style={styles.card}>
            <h2>Agent-rollen configuratie</h2>
            <p style={styles.beschrijving}>Pas de parameters per agent-rol aan met de schuifknoppen.</p>
            {loading ? <p>Laden...</p> : (
              <div style={styles.rollenGrid}>
                {agentRollen.map((rol) => (
                  <div key={rol.id} style={styles.rolCard}>
                    <div style={styles.rolHeader}>
                      <h3>{rol.agent_rol}</h3>
                      {rol.agent_rol !== 'discussieleider' && (
                        <label style={styles.checkboxLabel}>
                          <input 
                            type="checkbox" 
                            checked={rol.is_active} 
                            onChange={(e) => setAgentRollen(agentRollen.map(r => r.id === rol.id ? {...r, is_active: e.target.checked} : r))} 
                          />
                          Actief
                        </label>
                      )}
                      {rol.agent_rol === 'discussieleider' && (
                        <span style={{color: 'black'}}>Altijd actief</span>
                      )}
                    </div>
                    
                    <div style={styles.bronInfo}>
                      <strong>Bron:</strong> {rol.bron || '-'}
                    </div>
                    
                    <input 
                      type="text" 
                      value={rol.beschrijving || ''} 
                      onChange={(e) => setAgentRollen(agentRollen.map(r => r.id === rol.id ? {...r, beschrijving: e.target.value} : r))} 
                      placeholder="Beschrijving"
                      style={styles.rolInput}
                    />
                    
                    <div style={styles.sliderGroup}>
                      <label>Tokens factor: {rol.tokens_factor}</label>
                      <input 
                        type="range" 
                        min="0.5" 
                        max="2.0" 
                        step="0.1" 
                        value={rol.tokens_factor} 
                        onChange={(e) => setAgentRollen(agentRollen.map(r => r.id === rol.id ? {...r, tokens_factor: parseFloat(e.target.value)} : r))} 
                        style={styles.slider}
                      />
                    </div>
                    
                    {rol.agent_rol !== 'discussieleider' && (
                      <>
                        <div style={styles.sliderGroup}>
                          <label>Bronverwijzing: {rol.p_bronnen}</label>
                          <input 
                            type="range" 
                            min="0" 
                            max="1" 
                            step="0.1" 
                            value={rol.p_bronnen} 
                            onChange={(e) => setAgentRollen(agentRollen.map(r => r.id === rol.id ? {...r, p_bronnen: parseFloat(e.target.value)} : r))} 
                            style={styles.slider}
                          />
                        </div>
                        
                        <div style={styles.sliderGroup}>
                          <label>CV detail: {rol.p_cv_detail}</label>
                          <input 
                            type="range" 
                            min="0" 
                            max="1" 
                            step="0.1" 
                            value={rol.p_cv_detail} 
                            onChange={(e) => setAgentRollen(agentRollen.map(r => r.id === rol.id ? {...r, p_cv_detail: parseFloat(e.target.value)} : r))} 
                            style={styles.slider}
                          />
                        </div>
                        
                        <div style={styles.sliderGroup}>
                          <label>Referentie: {rol.p_referentie}</label>
                          <input 
                            type="range" 
                            min="0" 
                            max="1" 
                            step="0.1" 
                            value={rol.p_referentie} 
                            onChange={(e) => setAgentRollen(agentRollen.map(r => r.id === rol.id ? {...r, p_referentie: parseFloat(e.target.value)} : r))} 
                            style={styles.slider}
                          />
                        </div>
                      </>
                    )}
                    
                    <button onClick={() => updateAgentRol(rol)} style={styles.saveBtn}><Save size={14} /> Opslaan</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PROMPTS TAB */}
        {activeTab === 'prompts' && (
          <div style={styles.splitView}>
            <div style={styles.listPanel}>
              <div style={styles.panelHeader}>
                <h2>Prompts</h2>
                <button onClick={() => setShowPromptForm(true)} style={styles.addBtn}><Plus size={16} /> Nieuwe Prompt</button>
              </div>

              {/* Category filter */}
              <div style={{marginBottom: '16px'}}>
                <select
                  value={selectedPromptCategory}
                  onChange={(e) => {
                    setSelectedPromptCategory(e.target.value);
                    fetchPrompts(e.target.value || null);
                    setSelectedPrompt(null);
                  }}
                  style={styles.input}
                >
                  <option value="">Alle categorieën</option>
                  {promptCategories.map(cat => (
                    <option key={cat.key} value={cat.key}>{cat.name} ({cat.prompt_count})</option>
                  ))}
                </select>
              </div>

              {/* Seed button if no prompts */}
              {prompts.length === 0 && (
                <div style={{marginBottom: '16px'}}>
                  <button onClick={seedPrompts} style={styles.saveBtn}><RefreshCw size={14} /> Standaard prompts laden</button>
                </div>
              )}

              {showPromptForm && (
                <form onSubmit={createPromptHandler} style={styles.form}>
                  <select value={newPrompt.category} onChange={(e) => setNewPrompt({...newPrompt, category: e.target.value})} style={styles.input} required>
                    <option value="">-- Kies categorie --</option>
                    <option value="persona_chat">Persona Chat</option>
                    <option value="discussieleider">Discussieleider</option>
                    <option value="feedback">Feedback</option>
                    <option value="systeem">Systeem</option>
                  </select>
                  <input type="text" placeholder="Key (unieke identifier)" value={newPrompt.key} onChange={(e) => setNewPrompt({...newPrompt, key: e.target.value})} style={styles.input} required />
                  <input type="text" placeholder="Naam" value={newPrompt.name} onChange={(e) => setNewPrompt({...newPrompt, name: e.target.value})} style={styles.input} required />
                  <input type="text" placeholder="Beschrijving (optioneel)" value={newPrompt.description} onChange={(e) => setNewPrompt({...newPrompt, description: e.target.value})} style={styles.input} />
                  <textarea placeholder="Prompt tekst" value={newPrompt.prompt_text} onChange={(e) => setNewPrompt({...newPrompt, prompt_text: e.target.value})} style={{...styles.input, minHeight: '120px', fontFamily: 'monospace'}} required />
                  <input type="number" placeholder="Sorteervolgorde" value={newPrompt.sort_order} onChange={(e) => setNewPrompt({...newPrompt, sort_order: parseInt(e.target.value) || 0})} style={styles.input} />
                  <div style={styles.formButtons}>
                    <button type="submit" style={styles.saveBtn}><Save size={16} /> Opslaan</button>
                    <button type="button" onClick={() => setShowPromptForm(false)} style={styles.cancelBtn}>Annuleren</button>
                  </div>
                </form>
              )}

              {loading ? <p>Laden...</p> : prompts.length === 0 ? <p style={styles.empty}>Geen prompts gevonden.</p> : (
                <ul style={styles.list}>
                  {prompts.map((prompt) => (
                    <li key={prompt.id} style={selectedPrompt?.id === prompt.id ? styles.listItemActive : styles.listItem} onClick={() => setSelectedPrompt(prompt)}>
                      <div>
                        <span>{prompt.name}</span>
                        <div style={{fontSize: '12px', color: kleur.stil}}>{prompt.key}</div>
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        {prompt.is_system && <span style={styles.badge}>systeem</span>}
                        {!prompt.is_active && <span style={{...styles.badge, background: kleur.rood}}>inactief</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={styles.detailPanel}>
              {selectedPrompt ? (
                editingPrompt ? (
                  <div style={styles.editForm}>
                    <h2>Bewerken: {selectedPrompt.name}</h2>
                    <div style={{height: '12px'}}></div>

                    <label style={styles.label}>Naam</label>
                    <input type="text" value={editingPrompt.name || ''} onChange={(e) => setEditingPrompt({...editingPrompt, name: e.target.value})} style={styles.input} />

                    <label style={styles.label}>Beschrijving</label>
                    <input type="text" value={editingPrompt.description || ''} onChange={(e) => setEditingPrompt({...editingPrompt, description: e.target.value})} style={styles.input} />

                    <label style={styles.label}>Prompt tekst</label>
                    <textarea value={editingPrompt.prompt_text || ''} onChange={(e) => setEditingPrompt({...editingPrompt, prompt_text: e.target.value})} style={{...styles.input, minHeight: '300px', fontFamily: 'monospace', fontSize: '13px'}} />

                    <div style={styles.row}>
                      <div style={{flex: 1}}>
                        <label style={styles.label}>Sorteervolgorde</label>
                        <input type="number" value={editingPrompt.sort_order || 0} onChange={(e) => setEditingPrompt({...editingPrompt, sort_order: parseInt(e.target.value) || 0})} style={styles.input} />
                      </div>
                      <div style={{flex: 1}}>
                        <label style={styles.label}>Status</label>
                        <label style={{...styles.checkboxLabel, marginTop: '8px'}}>
                          <input type="checkbox" checked={editingPrompt.is_active} onChange={(e) => setEditingPrompt({...editingPrompt, is_active: e.target.checked})} />
                          Actief
                        </label>
                      </div>
                    </div>

                    <div style={{height: '16px'}}></div>
                    <div style={styles.formButtons}>
                      <button type="button" onClick={updatePrompt} style={styles.saveBtn}><Save size={16} /> Opslaan</button>
                      <button type="button" onClick={() => setEditingPrompt(null)} style={styles.cancelBtn}>Annuleren</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={styles.panelHeader}>
                      <h2>{selectedPrompt.name}</h2>
                      <div style={{display: 'flex', gap: '8px'}}>
                        <button onClick={() => setEditingPrompt({...selectedPrompt})} style={styles.addBtn}>Bewerken</button>
                        {selectedPrompt.is_system ? (
                          <button onClick={() => resetPrompt(selectedPrompt.id)} style={{...styles.addBtn, background: kleur.oranje}}><RefreshCw size={14} /> Reset</button>
                        ) : (
                          <button onClick={() => deletePrompt(selectedPrompt.id)} style={styles.deleteBtn}><Trash2 size={16} /> Verwijderen</button>
                        )}
                      </div>
                    </div>

                    <div style={{display: 'flex', gap: '8px', marginBottom: '16px'}}>
                      <span style={styles.badge}>{selectedPrompt.category}</span>
                      {selectedPrompt.is_system && <span style={{...styles.badge, background: kleur.paars}}>systeem</span>}
                      {selectedPrompt.is_active ? (
                        <span style={{...styles.badge, background: kleur.groen}}>actief</span>
                      ) : (
                        <span style={{...styles.badge, background: kleur.rood}}>inactief</span>
                      )}
                    </div>

                    <p style={styles.beschrijving}><strong>Key:</strong> {selectedPrompt.key}</p>
                    {selectedPrompt.description && <p style={styles.beschrijving}>{selectedPrompt.description}</p>}

                    <h3 style={styles.subheader}>Prompt tekst</h3>
                    <div style={{height: '8px'}}></div>
                    <pre style={styles.promptPreview}>{selectedPrompt.prompt_text}</pre>
                  </>
                )
              ) : <p style={styles.empty}>Selecteer een prompt</p>}
            </div>
          </div>
        )}

	{/* BEHEERDERS TAB */}
        {activeTab === 'beheerders' && (
          <div style={styles.splitView}>
            <div style={styles.listPanel}>
              <div style={styles.panelHeader}>
                <h2>Scholen</h2>
                <button onClick={() => setShowSchoolForm(true)} style={styles.addBtn}><Plus size={16} /> Nieuwe School</button>
              </div>
              {showSchoolForm && (
                <form onSubmit={createSchool} style={styles.form}>
                  <input type="text" placeholder="Brinnummer" value={newSchool.brinnummer} onChange={(e) => setNewSchool({...newSchool, brinnummer: e.target.value})} style={styles.input} required />
                  <input type="text" placeholder="Schoolnaam" value={newSchool.naam} onChange={(e) => setNewSchool({...newSchool, naam: e.target.value})} style={styles.input} required />
                  <div style={styles.formButtons}>
                    <button type="submit" style={styles.saveBtn}><Save size={16} /> Opslaan</button>
                    <button type="button" onClick={() => setShowSchoolForm(false)} style={styles.cancelBtn}>Annuleren</button>
                  </div>
                </form>
              )}
              {loading ? <p>Laden...</p> : schools.length === 0 ? <p style={styles.empty}>Nog geen scholen.</p> : (
                <ul style={styles.list}>
                  {schools.map((school) => (
                    <li key={school.id} style={selectedSchool?.id === school.id ? styles.listItemActive : styles.listItem} onClick={() => setSelectedSchool(school)}>
                      <span>{school.brinnummer} - {school.naam}</span>
                      <button onClick={(e) => { e.stopPropagation(); deleteSchool(school.id); }} style={styles.deleteBtn}><Trash2 size={14} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={styles.detailPanel}>
              {selectedSchool ? (
                <>
                  <div style={styles.panelHeader}>
                    <h2>{selectedSchool.naam}</h2>
                    <button onClick={() => setShowTeacherForm(true)} style={styles.addBtn}><Plus size={16} /> Nieuwe Leerkracht</button>
                  </div>
                  <p style={styles.beschrijving}>Brinnummer: {selectedSchool.brinnummer || '-'}</p>
                  
                  {showTeacherForm && (
                    <form onSubmit={createTeacher} style={styles.form}>
                      <input type="text" placeholder="Naam leerkracht" value={newTeacher.naam} onChange={(e) => setNewTeacher({...newTeacher, naam: e.target.value})} style={styles.input} required />
                      <input type="email" placeholder="E-mailadres" value={newTeacher.email} onChange={(e) => setNewTeacher({...newTeacher, email: e.target.value})} style={styles.input} required />
                      <input type="text" placeholder="Gebruikersnaam" value={newTeacher.username} onChange={(e) => setNewTeacher({...newTeacher, username: e.target.value})} style={styles.input} autoComplete="off" required />
                      <input type="password" placeholder="Wachtwoord" value={newTeacher.password} onChange={(e) => setNewTeacher({...newTeacher, password: e.target.value})} style={styles.input} autoComplete="new-password" required />
                      <div style={styles.formButtons}>
                        <button type="submit" style={styles.saveBtn}><Save size={16} /> Opslaan</button>
                        <button type="button" onClick={() => setShowTeacherForm(false)} style={styles.cancelBtn}>Annuleren</button>
                      </div>
                    </form>
                  )}
                  
		 <h3 style={styles.subheader}>Leerkrachten</h3>
                  <div style={{height: '12px'}}></div>
                  {editingTeacher ? (
                    <form onSubmit={updateTeacher} style={styles.form}>
                      <h4>Leerkracht bewerken</h4>
                      <div style={{height: '8px'}}></div>
                      <label style={styles.label}>Naam</label>
                      <input type="text" value={editingTeacher.naam} onChange={(e) => setEditingTeacher({...editingTeacher, naam: e.target.value})} style={styles.input} required />
                      <label style={styles.label}>Gebruikersnaam</label>
                      <input type="text" value={editingTeacher.username} onChange={(e) => setEditingTeacher({...editingTeacher, username: e.target.value})} style={styles.input} autoComplete="off" required />
                      <label style={styles.label}>Nieuw wachtwoord</label>
                      <input type="password" value={editingTeacher.newPassword || ''} onChange={(e) => setEditingTeacher({...editingTeacher, newPassword: e.target.value})} style={styles.input} autoComplete="new-password" placeholder="••••••••" />
                      <div style={styles.formButtons}>
                        <button type="submit" style={styles.saveBtn}><Save size={16} /> Opslaan</button>
                        <button type="button" onClick={() => setEditingTeacher(null)} style={styles.cancelBtn}>Annuleren</button>
                      </div>
                    </form>
                  ) : (
                    selectedSchool.teachers?.length === 0 ? <p style={styles.empty}>Nog geen leerkrachten.</p> : (
                      <ul style={styles.list}>
                        {selectedSchool.teachers?.map((teacher) => (
                          <li key={teacher.id} style={styles.listItem}>
                            <span>{teacher.naam} ({teacher.username})</span>
                            <div style={{display: 'flex', gap: '8px'}}>
                              <button onClick={() => setEditingTeacher({...teacher, newPassword: ''})} style={styles.addBtn}>Bewerken</button>
                              <button onClick={() => deleteTeacher(teacher.id)} style={styles.deleteBtn}><Trash2 size={14} /></button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )
                 )}
                </>
              ) : <p style={styles.empty}>Selecteer een school</p>}
            </div>
          </div>
        )}

        {/* LOGBESTANDEN TAB */}




        {activeTab === 'logs' && (
          <div style={styles.card}>
            <h2>Logbestanden downloaden</h2>
            <p style={styles.beschrijving}>Selecteer een school, klas en sessie om de activiteitenlog te downloaden.</p>
            <div style={{height: '16px'}}></div>
            
            <div style={styles.logFilters}>
              <div>
                <label style={styles.label}>School</label>
                <select 
                  value={selectedLogSchool} 
                  onChange={(e) => { 
                    setSelectedLogSchool(e.target.value); 
                    setSelectedLogClass('');
                    setLogSessions([]);
                    if (e.target.value) fetchLogClasses(e.target.value);
                  }} 
                  style={styles.input}
                >
                  <option value="">-- Kies een school --</option>
                  {logSchools.map(s => <option key={s.id} value={s.id}>{s.naam}</option>)}
                </select>
              </div>
              
              <div>
                <label style={styles.label}>Klas</label>
                <select 
                  value={selectedLogClass} 
                  onChange={(e) => { 
                    setSelectedLogClass(e.target.value);
                    if (e.target.value) fetchLogSessions(e.target.value);
                  }} 
                  style={styles.input}
                  disabled={!selectedLogSchool}
                >
                  <option value="">-- Kies een klas --</option>
                  {logClasses.map(c => <option key={c.id} value={c.id}>{c.naam}</option>)}
                </select>
              </div>
            </div>
            
            <div style={{height: '16px'}}></div>
            
            {logSessions.length > 0 && (
              <>
                <h3 style={styles.subheader}>Sessies</h3>
                <div style={{height: '12px'}}></div>
                <ul style={styles.list}>
                  {logSessions.map((session) => (
                    <li key={session.id} style={styles.listItem}>
                      {/* Een les heeft geen naam; de leerkracht herkent hem aan de
                          stelling en de datum. Dat is ook wat de server stuurt. */}
                      <span>
                        {session.stelling} — {session.datum}
                        {session.is_actief && <span style={{ ...styles.badge, marginLeft: '8px' }}>actief</span>}
                        <span style={{ color: kleur.stil, marginLeft: '8px', fontSize: '12px' }}>{session.aantal_groepjes} groepjes</span>
                      </span>
                      <button onClick={() => downloadLog(session.id)} style={styles.saveBtn}>Download</button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


const styles = {
  ...gedeeld,
  header: { background: 'white', padding: '16px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  userInfo: { display: 'flex', alignItems: 'center', gap: '16px' },
  tabs: { display: 'flex', background: 'white', borderBottom: '1px solid black', padding: '0 48px' },
  content: { padding: '24px 48px' },
  card: { background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid black' },
  addBtn: { padding: '8px 16px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
  input: { width: '100%', padding: '10px', border: `1px solid ${kleur.lijnZacht}`, borderRadius: '6px', fontSize: '14px', marginBottom: '10px', boxSizing: 'border-box' },
  inputSmall: { flex: 1, padding: '10px', border: `1px solid ${kleur.lijnZacht}`, borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' },
  row: { display: 'flex', gap: '10px', marginBottom: '10px' },
  formButtons: { display: 'flex', gap: '8px', marginTop: '10px' },
  saveBtn: { padding: '8px 16px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
  listItemActive: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', background: kleur.accentLichter, border: `2px solid ${kleur.accent}` },
  deleteBtn: { background: 'none', border: 'none', cursor: 'pointer', color: kleur.rood },
  beschrijving: { color: kleur.stil, marginBottom: '16px' },
  badge: { background: kleur.accent, color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' },
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px', padding: '16px', background: kleur.vlak, borderRadius: '8px' },
  formTabs: { display: 'flex', gap: '8px', marginBottom: '16px' },
  formTab: { flex: 1, padding: '10px', background: kleur.lijn, border: 'none', borderRadius: '6px', cursor: 'pointer' },
  formTabActive: { flex: 1, padding: '10px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  woordTeller: { fontSize: '13px', marginTop: '4px', marginBottom: '8px' },
  woordWarning: { fontWeight: 'bold' },
  bronnenGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' },
  bronnenColumn: { background: kleur.vlak, borderRadius: '8px', padding: '16px', cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent' },
  bronCategoryHeader: { margin: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' },
  bronCount: { background: kleur.accent, color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'normal' },
  bronPreviewText: { margin: 0, fontSize: '12px', color: kleur.stil },
  categoryModal: { marginTop: '20px', padding: '20px', background: 'white', border: `1px solid ${kleur.lijn}`, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  categoryModalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: `1px solid ${kleur.lijn}`, paddingBottom: '12px' },
  portretBlok: { border: `1px solid ${kleur.lijn}`, borderRadius: '10px', padding: '12px', marginBottom: '12px', background: kleur.vlakWarm },
  portretGalerij: { display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '8px 0 12px' },
  portretKeuze: { padding: 0, width: '56px', height: '56px', borderRadius: '50%', border: `2px solid ${kleur.lijn}`, background: 'white', cursor: 'pointer', overflow: 'hidden' },
  portretKeuzeActief: { padding: 0, width: '56px', height: '56px', borderRadius: '50%', border: `3px solid ${kleur.accent}`, background: 'white', cursor: 'pointer', overflow: 'hidden' },
  portretMini: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  portretLeeg: { width: '56px', height: '56px', borderRadius: '50%', border: `2px dashed ${kleur.lijnStippel}`, background: 'white', cursor: 'pointer', fontSize: '12px', color: kleur.grijs },
  portretLeegActief: { width: '56px', height: '56px', borderRadius: '50%', border: `3px solid ${kleur.accent}`, background: 'white', cursor: 'pointer', fontSize: '12px', color: kleur.tekst },
  portretUploadLabel: { display: 'block', fontSize: '13px', color: kleur.tekstZacht },
  portretFout: { color: kleur.roodDonker, fontSize: '13px', margin: '8px 0 0' },
  portretKop: { verticalAlign: 'middle', marginRight: '10px' },
  categoryBronList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  categoryBronItem: { background: kleur.vlak, borderRadius: '6px', padding: '12px', cursor: 'pointer', border: '2px solid transparent' },
  categoryBronItemActive: { border: `2px solid ${kleur.accent}`, background: kleur.accentLichter },
  categoryBronHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  categoryBronNaam: { fontWeight: 'bold', color: kleur.blauw },
  bronDetailPanel: { marginTop: '16px', padding: '16px', background: kleur.vlak, borderRadius: '8px', borderTop: `2px solid ${kleur.accent}` },
  bronDetailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  editBtn: { padding: '6px 12px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' },
  closeBtn: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: kleur.stil },
  bronTekst: { background: 'white', padding: '16px', borderRadius: '6px', whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '14px', maxHeight: '400px', overflow: 'auto', border: `1px solid ${kleur.lijn}` },
  rollenGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', marginTop: '20px' },
  rolCard: { background: kleur.vlak, borderRadius: '12px', padding: '20px', border: `1px solid ${kleur.lijn}` },
  rolHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  rolInput: { width: '100%', padding: '8px', border: `1px solid ${kleur.lijnZacht}`, borderRadius: '6px', marginBottom: '16px', boxSizing: 'border-box' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' },
  sliderGroup: { marginBottom: '16px' },
  slider: { width: '100%', height: '8px', borderRadius: '4px', appearance: 'none', background: kleur.lijn, outline: 'none', cursor: 'pointer' },
  label: { display: 'block', fontWeight: 'bold', marginBottom: '4px', marginTop: '12px', fontSize: '14px' },
  editForm: { maxHeight: '80vh', overflowY: 'auto' },
  logFilters: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  bronInfo: { padding: '8px', background: kleur.vlak, borderRadius: '4px', marginBottom: '12px', fontSize: '14px' },
  promptPreview: { background: kleur.vlak, padding: '16px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '400px', overflowY: 'auto', border: `1px solid ${kleur.lijn}` },
};

export default AdminPanel;

